/**
 * /backend/config/permissions.js
 *
 * Centralise les noms des permissions utilisés dans l'application backend.
 * Ces noms sont utilisés pour les vérifications dans les middlewares, services, etc.
 *
 * IMPORTANT : Ce fichier DOIT être maintenu synchronisé avec le fichier
 *             /frontend/web/src/config/permissions.js
 * les permisssions utilisees a ce jour dans le system 06042025
manage_notifications
access_admin_dashboard
manage_roles
manage_users
manage_categories
create_category
update_category
view_categories
delete_category
view_users
manage_system
 */
const PERMISSIONS = Object.freeze({
    // --- Accès Général ---
    ACCESS_ADMIN_PANEL: 'access_admin_panel',

    // --- Rôles & Permissions ---
    ASSIGN_ROLES: 'assign_roles',
    MANAGE_PERMISSIONS: 'manage_permissions',
    MANAGE_ROLE_PERMISSIONS: 'manage_role_permissions',
    MANAGE_ROLES: 'manage_roles',
    VIEW_ROLES: 'view_roles',

    // --- Réservations (Bookings) ---
    CANCEL_OWN_BOOKING: 'cancel_own_booking',
    CREATE_BOOKING_REQUEST: 'create_booking_request',
    MANAGE_ANY_BOOKING: 'manage_any_booking',
    MANAGE_OWN_BOOKING_REQUESTS: 'manage_own_booking_requests',
    VIEW_ALL_BOOKINGS: 'view_all_bookings',
    VIEW_OWN_BOOKING_REQUESTS: 'view_own_booking_requests',

    // --- Garanties & Paiements ---
    CONFIRM_GUARANTEE_RECEIVED: 'confirm_guarantee_received',
    CONFIRM_PAYMENT_RECEIVED: 'confirm_payment_received',
    INITIATE_GUARANTEE_PROCESS: 'initiate_guarantee_process',
    INITIATE_PAYMENT_CONFIRMATION: 'initiate_payment_confirmation',
    MANAGE_GUARANTEE_STATUS: 'manage_guarantee_status',
    MANAGE_TRANSACTIONS: 'manage_transactions',
    VIEW_TRANSACTION_INFO: 'view_transaction_info',

    // --- Catégories ---
    CATEGORY_CREATE: 'create_category',
    CATEGORY_UPDATE: 'update_category', // Ajout basé sur CategoryForm
    CATEGORY_DELETE: 'delete_category', // Ajout probable
    MANAGE_CATEGORIES: 'manage_categories',
    REVIEW_CATEGORY_SUBMISSIONS: 'review_category_submissions',
    SUBMIT_CATEGORY_FOR_REVIEW: 'submit_category_for_review',
    VIEW_CATEGORIES: 'view_categories',

    // --- Articles (Items) ---
    CREATE_ITEM: 'create_item',
    DELETE_ANY_ITEM: 'delete_any_item',
    DELETE_OWN_ITEM: 'delete_own_item',
    MANAGE_ITEM_AVAILABILITY: 'manage_item_availability',
    UPDATE_ANY_ITEM: 'update_any_item',
    UPDATE_OWN_ITEM: 'update_own_item',
    VIEW_ITEMS: 'view_items',

    // --- Avis (Reviews) ---
    CREATE_REVIEW: 'create_review',
    MANAGE_REVIEWS: 'manage_reviews',
    VIEW_REVIEWS: 'view_reviews',

    // --- Utilisateurs ---
    DELETE_USER: 'delete_user',
    MANAGE_USER_STATUS: 'manage_user_status',
    UPDATE_ANY_USER_PROFILE: 'update_any_user_profile',
    UPDATE_OWN_PROFILE: 'update_own_profile',
    VIEW_USER_PROFILE: 'view_user_profile',
    VIEW_USERS: 'view_users',
    // Note: create_users et edit_users manquaient dans la liste fournie, ajoutés pour cohérence
    USER_CREATE: 'create_users',
    USER_UPDATE: 'edit_users',
    MANAGE_USER:'manage_users',


    // --- Litiges (Disputes) ---
    MANAGE_DISPUTES: 'manage_disputes',
    REPORT_DISPUTE: 'report_dispute',
    RESOLVE_DISPUTES: 'resolve_disputes',
    VIEW_DISPUTES: 'view_disputes',

    // --- Notifications ---
    MANAGE_NOTIFICATION_PREFERENCES: 'manage_notification_preferences',
    MANAGE_NOTIFICATION_TEMPLATES: 'manage_notification_templates',
    NOTIFICATION_MANAGER_ALL: 'notification_manager_all',
    NOTIFICATION_MANAGER_CREATE: 'notification_manager_create',
    NOTIFICATION_MANAGER_DELETE: 'notification_manager_delete',
    NOTIFICATION_MANAGER_READ: 'notification_manager_read',
    NOTIFICATION_MANAGER_UPDATE: 'notification_manager_update',
    SEND_SYSTEM_NOTIFICATION: 'send_system_notification',
    VIEW_NOTIFICATIONS: 'view_notifications',
    MANAGE_NOTIFICATION:'manage_notifications',

    // --- Vérification ---
    MANAGE_VERIFICATION_STATUS: 'manage_verification_status',
    UPLOAD_VERIFICATION_DOCUMENT: 'upload_verification_document',
    VIEW_VERIFICATION_DOCUMENTS: 'view_verification_documents',

    // --- Messagerie ---
    MODERATE_MESSAGES: 'moderate_messages',
    SEND_MESSAGE: 'send_message',
    VIEW_MESSAGES: 'view_messages',

    // --- Validation des Listings ---
    VIEW_PENDING_LISTINGS: 'view_pending_listings',
    VALIDATE_LISTINGS: 'validate_listings',
    REJECT_LISTINGS: 'reject_listings',
    MANAGE_LISTING_STATUS: 'manage_listing_status',
    VIEW_LISTING_HISTORY: 'view_listing_history',

    // --- User ↔ Listings Management ---
    MANAGE_USER_LISTING: 'manage_user_listing',
    VIEW_USER_LISTING: 'view_user_listing',
    VIEW_USER_LISTING_STATS: 'view_user_listing_stats',
    SEARCH_USER_LISTING: 'search_user_listing',
    BULK_USER_LISTING_ACTIONS: 'bulk_user_listing_actions',
    SEND_USER_LISTING_EMAIL: 'send_user_listing_email',
    EXPORT_USER_LISTING_DATA: 'export_user_listing_data',

    // --- Système & Divers ---
    MANAGE_SYSTEM_SETTINGS: 'manage_system_settings',
    ADMIN_ACEESS_ADMIN_DASHBOARD: 'access_admin_dashboard',
    VIEW_ANALYTICS: 'view_analytics',
    VIEW_SYSTEM_LOGS: 'view_system_logs',
    VIEW_SYSTEM_SETTINGS: 'view_system_settings',
    MANAGE_SYSTEM: 'manage_system',

    // --- Documents ---
    GENERATE_DOCUMENT: 'generate_document',
    DOWNLOAD_DOCUMENT: 'download_document',
    PREVIEW_DOCUMENT: 'preview_document',
    MANAGE_DOCUMENTS: 'manage_documents',
    DELETE_DOCUMENT: 'delete_document',
    FINALIZE_DOCUMENT: 'finalize_document',
    REGENERATE_DOCUMENT: 'regenerate_document',
    DUPLICATE_DOCUMENT: 'duplicate_document',
    MERGE_DOCUMENTS: 'merge_documents',
    VIEW_DOCUMENT_STATS: 'view_document_stats',
    VIEW_DOCUMENT_METADATA: 'view_document_metadata',
    VERIFY_DOCUMENT: 'verify_document',
    VALIDATE_SIGNATURE: 'validate_signature',

    // --- Paiements ---
    CREATE_PAYMENT: 'create_payment',
    PROCESS_PAYMENT: 'process_payment',
    VIEW_PAYMENTS: 'view_payments',
    MANAGE_PAYMENTS: 'manage_payments',
    REFUND_PAYMENT: 'refund_payment',
    VIEW_PAYMENT_METHODS: 'view_payment_methods',
    MANAGE_PAYMENT_METHODS: 'manage_payment_methods',
    CREATE_DISPUTE: 'create_dispute',
    RESOLVE_PAYMENT_DISPUTE: 'resolve_payment_dispute',
    VIEW_PAYMENT_STATS: 'view_payment_stats',
    SYNC_STRIPE_PAYMENTS: 'sync_stripe_payments',
    MANAGE_STRIPE_WEBHOOKS: 'manage_stripe_webhooks',

    // --- Jobs Automatisés ---
    VIEW_AUTOMATED_JOBS: 'view_automated_jobs',
    MANAGE_AUTOMATED_JOBS: 'manage_automated_jobs',
    CANCEL_AUTOMATED_JOBS: 'cancel_automated_jobs',
    VIEW_AUTOMATED_JOB_STATISTICS: 'view_automated_job_statistics',
    CLEANUP_EXPIRED_JOBS: 'cleanup_expired_jobs',
    MONITOR_AUTOMATED_JOBS: 'monitor_automated_jobs',
    ACCESS_AUTOMATED_JOB_DETAILS: 'access_automated_job_details',

    // --- PHASE 5: Gestion Complète Admin ---
    MANAGE_RESERVATIONS: 'manage_reservations',
    VIEW_RESERVATION_ANALYTICS: 'view_reservation_analytics',
    EXPORT_RESERVATION_DATA: 'export_reservation_data',
    GENERATE_FINANCIAL_REPORTS: 'generate_financial_reports',
    ACCESS_BUSINESS_ANALYTICS: 'access_business_analytics',

    // --- Restrictions & Bannissements (Phase 7) ---
    VIEW_RESTRICTIONS: 'view_restrictions',
    APPLY_RESTRICTION: 'apply_restriction',
    LIFT_RESTRICTION: 'lift_restriction',
    VIEW_BANS: 'view_bans',
    BAN_USER_TEMPORARY: 'ban_user_temporary',
    BAN_USER_PERMANENT: 'ban_user_permanent',
    UNBAN_USER: 'unban_user',
    VIEW_STRIKES: 'view_strikes',
    ADD_STRIKE: 'add_strike',
    VOID_STRIKE: 'void_strike',
    VIEW_RESTRICTION_STATS: 'view_restriction_stats',
    EXPORT_RESTRICTION_DATA: 'export_restriction_data',
    MANAGE_RESTRICTION_THRESHOLDS: 'manage_restriction_thresholds'

});

// Exporter pour utilisation avec import ES6 dans React
export { PERMISSIONS };
export default PERMISSIONS;